import type {
  SupportAuthor,
  SupportMessage,
  SupportMessageReactionEvent,
  SupportMessageStatusEvent,
  SupportReplyPreview,
  SupportTicket,
} from './types'

/**
 * Чистая диалоговая логика, общая для клиентского чата (стор) и админки.
 * Обе стороны — один и тот же диалог, отличаются только тем, кто «свой»
 * (selfAuthor): не копируй эти функции в экраны, меняя лишь authorType.
 */

/**
 * Вставить/обновить тикет в списке и удержать сортировку по свежести.
 * merge решает, как совмещать старую и новую версии строки (по умолчанию —
 * замена; админ-список так сохраняет превью, которого нет в TICKET_UPDATED).
 */
export function upsertTicketIn<T extends SupportTicket>(
  list: T[],
  ticket: T,
  merge: (prev: T, next: T) => T = (_prev, next) => next,
): void {
  const index = list.findIndex(t => t.id === ticket.id)
  if (index === -1) list.unshift(ticket)
  else list[index] = merge(list[index], ticket)
  list.sort((a, b) => (a.lastMessageAt < b.lastMessageAt ? 1 : -1))
}

/**
 * Применить событие смены галочек к СВОИМ сообщениям (мутирует элементы).
 * delivered не затирает read: прочтение включает доставку, обратное — нет.
 */
export function applyStatusEventToMessages(
  messages: SupportMessage[],
  event: SupportMessageStatusEvent,
  selfAuthor: SupportAuthor,
  stamp: string = new Date().toISOString(),
): void {
  for (const message of messages) {
    if (message.ticketId !== event.ticketId || message.authorType !== selfAuthor) continue
    const targeted = event.allOwn || event.messageIds?.includes(message.id)
    if (!targeted) continue

    if (event.status === 'read') {
      message.readAt = message.readAt ?? stamp
      message.deliveredAt = message.deliveredAt ?? stamp
    } else {
      message.deliveredAt = message.deliveredAt ?? stamp
    }
  }
}

/**
 * Применить событие реакций (мутирует элемент). Сервер присылает ПОЛНЫЙ набор
 * реакций сообщения, а не дельту: локальное состояние заменяется целиком,
 * поэтому потерянное или продублированное событие не оставляет расхождения.
 */
export function applyReactionEventToMessages(
  messages: SupportMessage[],
  event: SupportMessageReactionEvent,
): void {
  const message = messages.find(m => m.id === event.messageId)
  if (!message) return
  message.reactions = event.reactions
}

/**
 * Цитата для оптимистичного пузыря: сервер построит её сам, но до ответа сети
 * ответ должен выглядеть ответом, а не обычным сообщением.
 */
export function toReplyPreview(message: SupportMessage): SupportReplyPreview {
  return {
    id: message.id,
    authorType: message.authorType,
    text: message.text,
    hasAttachments: message.attachments.length > 0,
    stickerId: message.stickerId,
  }
}

/** Своя реакция на сообщение (у стороны она одна) */
export function ownReaction(
  message: SupportMessage,
  selfAuthor: SupportAuthor,
): string | null {
  return message.reactions.find(r => r.authorType === selfAuthor)?.emoji ?? null
}

/**
 * Оптимистичный тоггл своей реакции: тот же эмодзи — снять, другой — заменить.
 * Догадка рисуется сразу, `next` уходит на сервер, `rollback` — на случай фейла.
 * Откат срабатывает ТОЛЬКО если набор не менялся с момента догадки: WS-событие
 * с полным набором, успевшее прийти раньше фейла, — более свежая истина.
 */
export function toggleOwnReaction(
  message: SupportMessage,
  selfAuthor: SupportAuthor,
  emoji: string,
): { next: string | null; rollback: () => void } {
  const previous = message.reactions
  const mine = ownReaction(message, selfAuthor)
  const next = mine === emoji ? null : emoji
  const optimistic = [
    ...previous.filter(r => r.authorType !== selfAuthor),
    ...(next ? [{ emoji: next, authorType: selfAuthor }] : []),
  ]
  message.reactions = optimistic
  return {
    next,
    rollback: () => {
      if (message.reactions === optimistic) message.reactions = previous
    },
  }
}

/**
 * Однострочное описание сообщения для цитаты и панели ответа.
 * Общее для пузыря и композера: иначе «Фото» и «Стикер» разъедутся по экранам.
 */
export function describeMessageForQuote(source: {
  text?: string | null
  hasAttachments?: boolean
  attachments?: unknown[]
  stickerId?: string | null
}): string {
  if (source.text) return source.text
  if (source.stickerId) return 'Стикер'
  const hasFiles = source.hasAttachments ?? (source.attachments?.length ?? 0) > 0
  return hasFiles ? 'Вложение' : 'Сообщение'
}

/**
 * Первое непрочитанное сообщение собеседника — перед ним рисуется планка
 * «Новые сообщения». Считать ДО markRead: после него readAt проставлен у всех.
 */
export function findNewMessagesAnchor(
  messages: SupportMessage[],
  selfAuthor: SupportAuthor,
): string | null {
  const other: SupportAuthor = selfAuthor === 'user' ? 'admin' : 'user'
  return messages.find(m => m.authorType === other && !m.readAt)?.id ?? null
}

/** Текст плашки «N новых …» с русским склонением */
export function formatNewMessagesCount(count: number): string {
  if (count === 1) return '1 новое сообщение'
  if (count < 5) return `${count} новых сообщения`
  return `${count} новых сообщений`
}
